import { useRef, useState } from 'react';
import { Editor } from '@revolist/react-datagrid';
import type { EditorType } from '@revolist/react-datagrid';
import { Box, MenuItem, Select, ThemeProvider, createTheme } from '@mui/material';
import type { Theme } from '@mui/material';

let sharedTheme: Theme | null = null;
const fallbackTheme = createTheme();

export const setEditorTheme = (theme: Theme) => {
  sharedTheme = theme;
};

type SelectEditorProps = EditorType & { options: readonly string[] };

export const FIELD_HEIGHT = 37;

const MENU_MAX_HEIGHT = 48 * 4.5 + 8;

const SelectCellEditor = ({ column, val, save, close, options }: SelectEditorProps) => {
  const initial = (val ?? column?.value ?? '') as string;
  const [open, setOpen] = useState(true);

  const savedRef = useRef(false);

  return (
    <ThemeProvider theme={sharedTheme ?? fallbackTheme}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: '100%',
          width: '100%',
          px: 2,
          boxSizing: 'border-box',
        }}
      >
        <Select
          open={open}
          autoFocus
          fullWidth
          size="small"
          value={options.includes(initial) ? initial : ''}
          onChange={(event) => {
            savedRef.current = true;
            save(event.target.value);
          }}
          onClose={() => {
            setOpen(false);
            if (!savedRef.current) {
              close();
            }
          }}
          sx={{
            height: FIELD_HEIGHT,
            fontSize: (theme) => theme.typography.body2.fontSize,
            backgroundColor: (theme) => theme.palette.background.default,
            '& .MuiSelect-select': {
              display: 'flex',
              alignItems: 'center',
              minHeight: 'unset',
              py: 0,
            },
          }}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: MENU_MAX_HEIGHT,
                backgroundColor: (sharedTheme ?? fallbackTheme).palette.background.default,
              },
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option} value={option} sx={{ fontSize: 'inherit' }}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </Box>
    </ThemeProvider>
  );
};

export const createSelectEditor = (options: readonly string[]) =>
  Editor((props: EditorType) => <SelectCellEditor {...props} options={options} />);
